import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Dialog } from '@factorialco/f0-react'
import {
  ActivityItemList,
  ActivityItemListProps,
  ActivityItemListSkeleton,
} from '@factorialco/f0-react/dist/experimental'
import { Settings as SettingsIcon } from '@factorialco/f0-react/icons/app'
import { ActivityActivityDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { track } from 'modules/core/lib/Tracking'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { EventTypes, dispatch } from 'modules/core/lib/pubSub'

import { useUnreadNotificationCount } from 'modules/notifications/hooks/useNotificationUnreadCount'
import { useNotificationsMarkAsRead } from 'modules/notifications/hooks/useNotificationsMarkAsRead'
import { events } from 'modules/notifications/lib/events'

import { getCategoryAndIcon, getRelativePath } from './utils'

export const NotificationActivity: ActivityComponent = () => {
  const { open, closeActivity } = useActivity()

  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const { update } = useNotificationsMarkAsRead()
  const { hasUnreadNotifications } = useUnreadNotificationCount()

  useEffect(() => {
    if (open) {
      track(events.notifications.viewNotifications)
    }
  }, [open])

  const {
    data,
    loading,
    controls: { fetchMore, loading: loadingFetchMore, hasMore },
  } = usePaginatedQuery(ActivityActivityDocument, {
    extractConnection: (dataToExtract) =>
      dataToExtract.notifications.inboxNotificationsConnection ?? [],
    skip: !open,
  })

  const dataItems = data?.notifications.inboxNotificationsConnection.edges.map((edge) => edge.node)

  const items: ActivityItemListProps['items'] = useMemo(
    () =>
      dataItems?.map((item) => {
        const { icon, category } = getCategoryAndIcon(item.notifier)

        return {
          id: `${item.id}`,
          title: item.title,
          description: item.body === item.title ? undefined : item.body,
          category,
          icon,
          createdAt: stringToDate(item.createdAt, 'backend_datetime').toDate(),
          isUnread: !item.readAt && hasUnreadNotifications,
        }
      }) ?? [],
    [dataItems, hasUnreadNotifications]
  )

  const loadingMore = loadingFetchMore && !!items.length
  useEffect(() => {
    if (open && loadingMore) {
      track(events.notifications.loadMoreNotifications)
    }
  }, [open, loadingMore])

  const lastItem = items?.[0]

  const lastItemId = useRef(lastItem?.id)

  useEffect(() => {
    if (lastItem?.id) {
      lastItemId.current = lastItem.id
    }
  }, [lastItem?.id])

  const handleMarkAllAsRead = useCallback(async () => {
    if (!lastItemId.current || open || !hasUnreadNotifications) return

    await update(parseInt(lastItemId.current, 10))
    dispatch({ type: EventTypes.INBOX_NOTIFICATIONS_READ })
  }, [update, open, hasUnreadNotifications])

  const wasOpened = useRef(open)

  useEffect(() => {
    if (!open && wasOpened.current) {
      handleMarkAllAsRead()
    }

    wasOpened.current = open
  }, [open, handleMarkAllAsRead])

  const handleListEndReached = useCallback(() => {
    if (!hasMore) return

    fetchMore()
  }, [hasMore, fetchMore])

  const handleClickItem = useCallback(
    (itemId: string) => {
      const item = dataItems?.find((it) => `${it.id}` === itemId)

      if (!item || !item.webResourceUrl) return

      navigate(getRelativePath(item.webResourceUrl))
    },
    [dataItems, navigate]
  )

  const showLoader = !dataItems?.length && loading

  useEffect(() => {
    if (!showLoader && !items.length && open) {
      track(events.notifications.viewEmptyNotifications)
    }
  }, [showLoader, items.length, open])

  const handleClickSettings = () => {
    navigate(navigation.inbox.settings.fullPath())
  }

  const otherActions = [
    {
      label: i18n.t('notifications.inbox.settings'),
      icon: SettingsIcon,
      onClick: handleClickSettings,
    },
  ]

  return (
    <F0Dialog
      isOpen={open}
      onClose={closeActivity}
      position="left"
      title={i18n.t('common.notifications')}
      otherActions={otherActions}
    >
      {showLoader ? (
        <ActivityItemListSkeleton />
      ) : (
        <ActivityItemList
          items={items}
          onClickItem={handleClickItem}
          onEndReached={handleListEndReached}
          loadingMoreItems={loadingMore}
        />
      )}
    </F0Dialog>
  )
}
